/* ============================================================
   global-scripts.js — Sharks
   Shared theme behaviour. Data-attribute driven.
   Header hooks:
     [data-header]            header root
     [data-menu-toggle]       hamburger — toggles the slide-in nav
     [data-nav]               nav container (slide-in panel on mobile)
     [data-menu-item]         nav item with dropdown
     [data-dropdown-trigger]  dropdown toggle (desktop panel / mobile accordion)
     [data-dropdown]          dropdown panel (animated on mobile)
   Footer hooks:
     [data-footer-accordion]  footer menu column
     [data-footer-toggle]     column trigger (mobile only)
     [data-footer-panel]      collapsible link panel (animated on mobile)
   Form hooks:
     [data-field]             floating-label wrapper (.field + .field_label)
     [data-validate]          client-side validation, on the form OR a wrapper
                              (the form still needs `novalidate`)
     [data-field-error]       message row inside a field (auto-created if absent)
     [data-form-banner]       dismissible form-level message
                              (optional data-auto-dismiss="<ms>")
     [data-banner-message]    text slot inside a banner
     [data-banner-close]      close button inside a banner
     [data-mailchimp-form]    posts to Mailchimp over JSONP instead of Shopify
                              (data-success-message / data-error-message)
   ============================================================ */

const BREAKPOINTS = { mobile: 768, nav: 992, tablet: 1024, footer: 992 };
/* The header goes mobile below 992 in base.css (slide-in panel + max-height
   accordions), so the nav JS has to use 992 too — at 768 the 768–991 band gets
   a collapsed dropdown whose trigger toggles classes but never animates. */
const isDesktop = () => window.innerWidth >= BREAKPOINTS.nav;
/* Footer columns go horizontal at 992 in base.css, not 768 — keep the JS in step
   or the 768–991 band renders a collapsed accordion whose trigger does nothing. */
const isFooterDesktop = () => window.innerWidth >= BREAKPOINTS.footer;

function debounce(fn, delay = 150) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/* ----- Accordion animation (max-height) ------------------ */
function expandPanel(panel) {
  panel.style.maxHeight = panel.scrollHeight + 'px';
  const onEnd = (e) => {
    if (e.propertyName !== 'max-height') return;
    panel.style.maxHeight = 'none';
    panel.removeEventListener('transitionend', onEnd);
  };
  panel.addEventListener('transitionend', onEnd);
}
function collapsePanel(panel) {
  panel.style.maxHeight = panel.scrollHeight + 'px';
  panel.getBoundingClientRect();
  panel.style.maxHeight = '0px';
}
function resetPanel(panel) {
  panel.style.maxHeight = '';
}

/* ----- Mobile slide-in nav ------------------------------- */
function initMobileNav() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const nav = document.querySelector('[data-nav]');
  if (!toggle || !nav || toggle.dataset.menuInit) return;
  toggle.dataset.menuInit = 'true';

  const setState = (open) => {
    toggle.classList.toggle('is-active', open);
    nav.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    nav.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  toggle.addEventListener('click', () => setState(!nav.classList.contains('is-open')));
  toggle.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setState(!nav.classList.contains('is-open'));
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setState(false);
  });
}

/* ----- Header: solid background + hide-on-scroll-down ----- */
function initHeaderScroll() {
  const header = document.querySelector('[data-header]');
  if (!header || header.dataset.scrollInit) return;
  header.dataset.scrollInit = 'true';

  // The fixed wrapper is what slides out of view; fall back to the root.
  const bar = header.closest('.header_main') || header;

  const SOLID_AT = 10;     // px scrolled before the solid background fades in
  const HIDE_AFTER = 200;  // px scrolled before the bar is allowed to hide
  const DELTA = 6;         // px of movement to ignore (jitter)
  let lastY = window.scrollY;
  let ticking = false;

  const update = () => {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > SOLID_AT);

    const diff = y - lastY;
    // Near the top, or while the mobile menu is open, always show the bar.
    if (y <= HIDE_AFTER || document.body.classList.contains('menu-open')) {
      bar.classList.remove('is-hidden');
    } else if (Math.abs(diff) > DELTA) {
      // Scrolling down hides it; scrolling up slides it back down.
      bar.classList.toggle('is-hidden', diff > 0);
    }

    if (Math.abs(diff) > DELTA) lastY = y;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }, { passive: true });

  update();
}

/* ----- Header dropdowns (desktop panels + mobile accordions) */
function initDropdowns() {
  const items = document.querySelectorAll('[data-menu-item]');

  const closeAll = (except) => {
    items.forEach((item) => {
      if (item === except || !item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('[data-dropdown]');
      if (panel && !isDesktop()) collapsePanel(panel);
    });
  };

  items.forEach((item) => {
    const trigger = item.querySelector('[data-dropdown-trigger]');
    if (!trigger || trigger.dataset.dropdownInit) return;
    trigger.dataset.dropdownInit = 'true';

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const panel = item.querySelector('[data-dropdown]');
      const open = item.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Accordion: opening one closes the rest (desktop panels + mobile accordions).
      if (open) closeAll(item);
      if (!isDesktop() && panel) {
        open ? expandPanel(panel) : collapsePanel(panel);
      }
    });

    // Desktop is hover-driven, but a click leaves a sticky `is-open` behind (kept
    // for keyboard users, who open with Enter and have no hover). Without these,
    // hovering a second item shows it via :hover while the clicked one stays open
    // — two panels at once. So the pointer takes over from the click: entering any
    // item drops every other item's sticky state, leaving closes this one.
    item.addEventListener('mouseenter', () => {
      if (isDesktop()) closeAll(item);
    });

    item.addEventListener('mouseleave', () => {
      if (!isDesktop() || !item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      // The trigger still holds focus after the click, and CSS also opens on
      // :focus-within — so drop focus too or the panel never actually hides.
      if (item.contains(document.activeElement)) document.activeElement.blur();
    });
  });

  if (!document.body.dataset.dropdownGlobalInit) {
    document.body.dataset.dropdownGlobalInit = 'true';
    document.addEventListener('click', (e) => {
      if (isDesktop() && !e.target.closest('[data-menu-item]')) closeAll(null);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isDesktop()) closeAll(null);
    });
  }
}

/* ----- Footer accordions (mobile only) ------------------- */
function initFooterAccordions() {
  const cols = document.querySelectorAll('[data-footer-accordion]');

  const closeOthers = (except) => {
    cols.forEach((col) => {
      if (col === except || !col.classList.contains('is-open')) return;
      col.classList.remove('is-open');
      const trigger = col.querySelector('[data-footer-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      const panel = col.querySelector('[data-footer-panel]');
      if (panel) collapsePanel(panel);
    });
  };

  cols.forEach((col) => {
    const trigger = col.querySelector('[data-footer-toggle]');
    const panel = col.querySelector('[data-footer-panel]');
    if (!trigger || !panel || trigger.dataset.accInit) return;
    trigger.dataset.accInit = 'true';

    trigger.addEventListener('click', () => {
      if (isFooterDesktop()) return;
      const open = col.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Accordion: opening one column closes the others.
      if (open) closeOthers(col);
      open ? expandPanel(panel) : collapsePanel(panel);
    });
  });
}

/* Reset accordion state + inline heights so desktop renders cleanly */
function resetAccordions(itemSelector, triggerSelector, panelSelector) {
  document.querySelectorAll(itemSelector).forEach((item) => {
    item.classList.remove('is-open');
    const trigger = item.querySelector(triggerSelector);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    const panel = item.querySelector(panelSelector);
    if (panel) resetPanel(panel);
  });
}

/* ----- Floating labels ----------------------------------- */
/* Default behaviour for text fields: wrap a control in .field[data-field] with a
   .field_label; the label lifts on focus and stays lifted while the field holds
   a value. Per-control guard means re-running init() (on resize) never doubles
   the listeners. */
function initFloatingLabels(scope = document) {
  scope.querySelectorAll('[data-field]').forEach((field) => {
    const control = field.querySelector('input, textarea, select');
    if (!control || control.dataset.floatInit === 'true') return;
    control.dataset.floatInit = 'true';

    const sync = () => {
      const filled = document.activeElement === control || control.value.trim() !== '';
      field.classList.toggle('is-filled', filled);
    };

    control.addEventListener('focus', () => field.classList.add('is-filled'));
    control.addEventListener('blur', sync);
    control.addEventListener('input', sync);
    control.addEventListener('change', sync);

    sync();
    // Browser autofill can land a value with no input event — re-check once.
    setTimeout(sync, 250);
  });
}

/* ----- Form validation ----------------------------------- */
/* Opt in per form with <form data-validate novalidate>. On submit each control's
   native constraints (required, type=email, pattern, minlength…) are checked and
   an inline message is shown under the field; errors clear as the user edits.

   Custom copy, in priority order:
     data-error-required  — shown when the field is empty
     data-error-type      — shown for a type/format mismatch (e.g. email)
     data-error-pattern   — shown for a pattern mismatch
     data-error           — generic fallback for any failure
   With none set it falls back to the browser's own (localized) message. */
const ERROR_ICON =
  '<svg class="field_error-icon" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
  '<circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.2"/>' +
  '<path d="M7 3.8v3.4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
  '<circle cx="7" cy="9.7" r="0.8" fill="currentColor"/></svg>';

function initFormValidation(scope = document) {
  /* Hook the form directly, or a wrapper — the Liquid {% form %} tag can't take a
     hyphenated attribute, so sections mark the surrounding element instead. */
  const forms = new Set();
  scope.querySelectorAll('form[data-validate]').forEach((f) => forms.add(f));
  scope.querySelectorAll('[data-validate] form').forEach((f) => forms.add(f));

  forms.forEach((form) => {
    if (form.dataset.validateInit === 'true') return;
    form.dataset.validateInit = 'true';

    const fieldOf = (control) => control.closest('[data-field]') || control.parentElement;
    const controls = () =>
      form.querySelectorAll('input:not([type="hidden"]), textarea, select');

    function messageFor(control) {
      const v = control.validity;
      const d = control.dataset;
      if (v.valueMissing && d.errorRequired) return d.errorRequired;
      if (v.typeMismatch && d.errorType) return d.errorType;
      if (v.patternMismatch && d.errorPattern) return d.errorPattern;
      return d.error || control.validationMessage;
    }

    function showError(control) {
      const field = fieldOf(control);
      if (!field) return;
      field.classList.add('has-error');
      let msg = field.querySelector('[data-field-error]');
      if (!msg) {
        msg = document.createElement('p');
        msg.className = 'field_error';
        msg.setAttribute('data-field-error', '');
        msg.setAttribute('role', 'alert');
        msg.innerHTML = ERROR_ICON + '<span data-field-error-text></span>';
        field.appendChild(msg);
      }
      msg.querySelector('[data-field-error-text]').textContent = messageFor(control);
    }

    function clearError(control) {
      const field = fieldOf(control);
      if (field) field.classList.remove('has-error');
    }

    form.addEventListener('submit', (e) => {
      let firstInvalid = null;
      controls().forEach((control) => {
        if (control.disabled) return;
        if (control.checkValidity()) {
          clearError(control);
        } else {
          showError(control);
          if (!firstInvalid) firstInvalid = control;
        }
      });
      if (firstInvalid) {
        e.preventDefault();
        firstInvalid.focus();
      }
    });

    controls().forEach((control) => {
      if (control.dataset.validateBound === 'true') return;
      control.dataset.validateBound = 'true';
      const clear = () => clearError(control);
      control.addEventListener('input', clear);
      control.addEventListener('change', clear);
    });
  });
}

/* ----- Form banners -------------------------------------- */
/* Dismissible form-level message. Closes on click, or after
   data-auto-dismiss ms (omit the attribute to keep it until closed).
   Render it hidden and call showFormBanner(), or render it visible to
   have the timer start on load. */
const bannerTimers = new WeakMap();

function dismissFormBanner(banner) {
  clearTimeout(bannerTimers.get(banner));
  banner.classList.add('is-dismissing');
  const onEnd = (e) => {
    if (e.propertyName !== 'opacity') return;
    banner.removeEventListener('transitionend', onEnd);
    /* A re-show mid-fade clears the flag — don't hide the new message. */
    if (banner.classList.contains('is-dismissing')) banner.hidden = true;
  };
  banner.addEventListener('transitionend', onEnd);
}

function armFormBanner(banner) {
  const delay = parseInt(banner.dataset.autoDismiss, 10);
  clearTimeout(bannerTimers.get(banner));
  if (delay > 0) bannerTimers.set(banner, setTimeout(() => dismissFormBanner(banner), delay));
}

function showFormBanner(banner, message, variant = 'success') {
  const slot = banner.querySelector('[data-banner-message]') || banner;
  slot.textContent = message;
  banner.classList.remove('form-banner--success', 'form-banner--error', 'is-dismissing');
  banner.classList.add('form-banner--' + variant);
  banner.setAttribute('role', variant === 'error' ? 'alert' : 'status');
  banner.hidden = false;
  armFormBanner(banner);
}

function initFormBanners(scope = document) {
  scope.querySelectorAll('[data-form-banner]').forEach((banner) => {
    if (banner.dataset.bannerInit === 'true') return;
    banner.dataset.bannerInit = 'true';

    const close = banner.querySelector('[data-banner-close]');
    if (close) close.addEventListener('click', () => dismissFormBanner(banner));

    if (!banner.hidden) armFormBanner(banner);
  });
}

/* ----- Mailchimp signup ---------------------------------- */
/* Posts to Mailchimp's post-json endpoint over JSONP — that endpoint sends no
   CORS headers, so fetch() is not an option. Nothing is stored in Shopify.
   Runs after initFormValidation, so a failed validation (which preventDefaults)
   stops the request. */
function mailchimpJsonp(url) {
  return new Promise((resolve, reject) => {
    const callback = 'mcCallback_' + Math.floor(Math.random() * 1e9);
    const script = document.createElement('script');
    const cleanup = () => {
      delete window[callback];
      script.remove();
    };
    window[callback] = (data) => {
      cleanup();
      resolve(data);
    };
    script.onerror = () => {
      cleanup();
      reject(new Error('Mailchimp request failed'));
    };
    script.src = url + '&c=' + callback;
    document.head.appendChild(script);
  });
}

/* Mailchimp prefixes messages with a field code ("0 - ...") and can return
   HTML (the "already subscribed" reply links to its own update page). */
function cleanMailchimpMessage(msg) {
  return String(msg || '')
    .replace(/^\d+\s*-\s*/, '')
    .replace(/<[^>]*>/g, '')
    .trim();
}

function initMailchimpForms(scope = document) {
  scope.querySelectorAll('form[data-mailchimp-form]').forEach((form) => {
    if (form.dataset.mailchimpInit === 'true') return;
    form.dataset.mailchimpInit = 'true';

    const banner = form.querySelector('[data-form-banner]');
    const submit = form.querySelector('[type="submit"]');

    form.addEventListener('submit', async (e) => {
      /* Validation already blocked it — leave the field errors on screen. */
      if (e.defaultPrevented) return;
      e.preventDefault();

      /* form.action falls back to the page URL when the attribute is blank.
         Mailchimp's embed code shows &amp; — tolerate a copy-paste of it. */
      const action = (form.getAttribute('action') || '').replace(/&amp;/g, '&');
      let endpoint;
      try {
        endpoint = new URL(action);
      } catch {
        console.warn('Newsletter: no Mailchimp form URL set on the footer section.');
        if (banner) showFormBanner(banner, form.dataset.errorMessage, 'error');
        return;
      }
      endpoint.pathname = endpoint.pathname.replace(/\/post(-json)?$/, '/post-json');

      const params = new URLSearchParams(endpoint.search);
      new FormData(form).forEach((value, key) => params.set(key, value));
      /* Mailchimp's bot trap: a field named b_<u>_<id> that must arrive empty. */
      const u = params.get('u');
      const id = params.get('id');
      if (u && id) params.set('b_' + u + '_' + id, '');

      if (submit) submit.disabled = true;
      form.setAttribute('aria-busy', 'true');

      try {
        const data = await mailchimpJsonp(endpoint.origin + endpoint.pathname + '?' + params);
        if (data.result === 'success') {
          if (banner) showFormBanner(banner, form.dataset.successMessage, 'success');
          resetForm(form);
        } else if (banner) {
          showFormBanner(banner, cleanMailchimpMessage(data.msg) || form.dataset.errorMessage, 'error');
        }
      } catch (err) {
        console.warn('Newsletter:', err);
        if (banner) showFormBanner(banner, form.dataset.errorMessage, 'error');
      } finally {
        if (submit) submit.disabled = false;
        form.removeAttribute('aria-busy');
      }
    });
  });
}

/* Clear the fields and let the floating labels and field errors catch up. */
function resetForm(form) {
  form.reset();
  form.querySelectorAll('input, textarea, select').forEach((control) => {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/* ----- Init ---------------------------------------------- */
function init() {
  initMobileNav();
  initHeaderScroll();
  initDropdowns();
  initFooterAccordions();
  initFloatingLabels();
  initFormValidation();
  initFormBanners();
  initMailchimpForms();

  if (isDesktop()) {
    document.body.classList.remove('menu-open');
    const nav = document.querySelector('[data-nav]');
    const toggle = document.querySelector('[data-menu-toggle]');
    if (nav) nav.classList.remove('is-open');
    if (toggle) {
      toggle.classList.remove('is-active');
      toggle.setAttribute('aria-expanded', 'false');
    }
    resetAccordions('[data-menu-item]', '[data-dropdown-trigger]', '[data-dropdown]');
  }

  if (isFooterDesktop()) {
    resetAccordions('[data-footer-accordion]', '[data-footer-toggle]', '[data-footer-panel]');
  }
}

document.addEventListener('DOMContentLoaded', init);
window.addEventListener('resize', debounce(init));
/* Re-scan when a section is re-rendered in the theme editor. */
document.addEventListener('shopify:section:load', (e) => {
  initFloatingLabels(e.target);
  initFormValidation(e.target);
  initFormBanners(e.target);
  initMailchimpForms(e.target);
});
